import { createContext, type ReactNode, useContext, useEffect, useRef } from 'react';

import type { ChartData } from '@/types/chart';

type ExportCallback = (blob: Blob, filename: string) => void;
type ExportFn = (callback: ExportCallback) => void;

interface ChartContextValue {
  chartData: ChartData | null;
  setChartData: (data: ChartData | null) => void;
  exportFn: {
    set: (fn: ExportFn) => void;
    get: () => ExportFn | null;
  };
}

const ChartContext = createContext<ChartContextValue | null>(null);

export function useChartContext() {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error('useChartContext must be used within ChartProvider');
  }
  return context;
}

/**
 * Hook to automatically set chart data and title
 * Updates both navbar and browser tab title
 */
export function useChartTitle(chartData: ChartData | null) {
  const { setChartData } = useChartContext();

  useEffect(() => {
    setChartData(chartData);
  }, [chartData, setChartData]);
}

export function ChartProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: Omit<ChartContextValue, 'exportFn'>;
}) {
  const exportFnRef = useRef<ExportFn | null>(null);

  const fullValue: ChartContextValue = {
    ...value,
    exportFn: {
      set: (fn: ExportFn) => {
        exportFnRef.current = fn;
      },
      get: () => exportFnRef.current,
    },
  };

  return <ChartContext.Provider value={fullValue}>{children}</ChartContext.Provider>;
}
